import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Star, Upload, Loader2, Trophy, BarChart3, Trash2, Archive, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel,
} from "./ui/select";
import { api } from "../lib/api";
import { usePlatform, platformStyle } from "../lib/ridersPlatform";

const AR_MONTHS = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
const monthLabel = (ym) => {
  const [y, m] = String(ym).split("-");
  return `${AR_MONTHS[parseInt(m, 10) - 1] || m} ${y}`;
};

const GRADE_CLS = {
  A: "bg-emerald-100 text-emerald-700", B: "bg-teal-100 text-teal-700",
  C: "bg-amber-100 text-amber-700", D: "bg-orange-100 text-orange-700",
  E: "bg-rose-100 text-rose-600", F: "bg-red-100 text-red-700",
};

const thisMonth = () => new Date().toISOString().slice(0, 7);

export const MonthlyEvaluationSection = ({ canEdit, onSelectEmployee }) => {
  const [platform] = usePlatform();
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState({ items: [], distribution: {}, months: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const r = await api.evaluationsOverview(m, platform);
      setData(r);
      if (r.month && r.month !== m) setMonth(r.month);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [platform]);
  useEffect(() => { load(month); /* eslint-disable-next-line */ }, [platform]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const r = await api.importEvaluations(month, file, platform);
      const total = (r.matched || 0) + (r.unmatched || 0);
      toast.success(`تم تسجيل تقييم ${total} كابتن${r.matched ? ` · ${r.matched} مرتبط بموظف` : ""}${r.unmatched ? ` · ${r.unmatched} برقم الكابتن` : ""} — منصة ${platform}`);
      load(month);
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر رفع الملف"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onDeleteMonth = async () => {
    if (!window.confirm(`سيتم حذف كل تقييمات شهر ${month} لمنصة ${platform}. متابعة؟`)) return;
    setBusy(true);
    try {
      const r = await api.deleteMonthEvaluations(month, platform);
      toast.success(`تم حذف ${r.deleted} تقييم لشهر ${month}`);
      load(month);
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر الحذف"); }
    finally { setBusy(false); }
  };

  const onDeleteOne = async (e, it) => {
    e.stopPropagation();
    if (!window.confirm(`حذف تقييم «${it.name}» لشهر ${month}؟`)) return;
    try {
      await api.deleteEmployeeMonthEvaluation(it.employee_id, month, platform);
      toast.success("تم حذف التقييم");
      load(month);
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر الحذف"); }
  };

  const dist = data.distribution || {};
  return (
    <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="monthly-evaluation-section">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="font-heading font-bold text-slate-800 flex items-center gap-2">
          <Star className="h-5 w-5 text-amber-500" /> التقييم الأسبوعي للمناديب
          <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold"
            style={{ background: platformStyle(platform).bg, color: platformStyle(platform).text }} data-testid="eval-platform-badge">
            {platform}
          </span>
        </h3>
        <div className="flex items-center gap-2">
          {(data.months || []).length > 0 && (
            <Select value={(data.months || []).includes(month) ? month : ""}
              onValueChange={(m) => { setMonth(m); load(m); }}>
              <SelectTrigger data-testid="eval-month-select" className="h-9 w-44 gap-1.5">
                <Archive className="h-4 w-4 text-amber-500" />
                <SelectValue placeholder="أشهر بها تقييمات" />
              </SelectTrigger>
              <SelectContent>
                {(() => {
                  const months = data.months || [];
                  const latest = months[0];
                  const older = months.slice(1);
                  return (
                    <>
                      <SelectGroup>
                        <SelectLabel className="text-[11px] text-slate-400 font-normal">الأحدث</SelectLabel>
                        <SelectItem value={latest}>{monthLabel(latest)}</SelectItem>
                      </SelectGroup>
                      {older.length > 0 && (
                        <>
                          <div role="button" tabIndex={0} data-testid="eval-archive-toggle"
                            onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setArchiveOpen((o) => !o); }}
                            className="flex items-center justify-between gap-2 px-2 py-2 mt-1 border-t border-slate-100 text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-50 rounded-md select-none">
                            <span className="flex items-center gap-1.5"><Archive className="h-3.5 w-3.5" /> الأرشيف — أشهر سابقة ({older.length})</span>
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${archiveOpen ? "rotate-180" : ""}`} />
                          </div>
                          {archiveOpen && (
                            <SelectGroup>
                              {older.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
                            </SelectGroup>
                          )}
                        </>
                      )}
                    </>
                  );
                })()}
              </SelectContent>
            </Select>
          )}
          <Input data-testid="eval-month-picker" type="month" value={month}
            onChange={(e) => { setMonth(e.target.value); load(e.target.value); }} className="h-9 w-40" title="اختر أي شهر" />
          {canEdit && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="hidden" data-testid="eval-file-input" />
              <Button data-testid="eval-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()} className="bg-blue-600 hover:bg-blue-700 gap-1.5 h-9">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} رفع تقييم Excel
              </Button>
              {data.count > 0 && (
                <Button data-testid="eval-delete-month-btn" disabled={busy} onClick={onDeleteMonth} variant="outline"
                  className="gap-1.5 h-9 border-rose-200 text-rose-600 hover:bg-rose-50">
                  <Trash2 className="h-4 w-4" /> حذف تقييمات الشهر
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-8 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
      ) : data.count === 0 ? (
        <div className="py-8 text-center text-slate-400 text-sm">
          <BarChart3 className="mx-auto h-8 w-8 text-slate-300 mb-1" />
          لا توجد تقييمات لهذا الشهر — ارفع ملف Excel (عمود الاسم + عمود التقييم) وسيُربط تلقائياً بالمناديب.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-4">
            <div className="rounded-xl bg-blue-50 border border-blue-100 p-2.5 text-center col-span-2">
              <div className="text-[11px] text-blue-500">متوسط التقييم</div>
              <div className="font-heading font-bold text-blue-800 font-mono-nums">{data.avg_score ?? "—"} / 5</div>
            </div>
            {["A", "B", "C", "D", "E", "F"].map((g) => (
              <div key={g} className={`rounded-xl border p-2.5 text-center ${GRADE_CLS[g]} border-transparent`}>
                <div className="font-heading font-bold text-lg">{g}</div>
                <div className="text-[11px] font-mono-nums">{dist[g] || 0}</div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-2 bg-slate-50 text-sm font-semibold text-slate-600 flex items-center gap-1.5"><Trophy className="h-4 w-4 text-amber-500" /> ترتيب المناديب ({data.count})</div>
            <div className="divide-y divide-slate-50 max-h-80 overflow-y-auto">
              {data.items.map((it, idx) => (
                <button key={it.eval_id || it.employee_id} data-testid={`eval-row-${it.employee_id || it.rider_id}`}
                  onClick={() => it.employee_id && onSelectEmployee?.(it.employee_id)}
                  className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition text-right">
                  <div className="flex items-center gap-2.5">
                    <span className="text-xs text-slate-400 font-mono-nums w-5">{idx + 1}</span>
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{it.name}{!it.employee_id && <span className="mr-1.5 text-[10px] font-normal text-slate-400">(رقم كابتن)</span>}</div>
                      <div className="text-xs text-slate-400">{it.branch || "—"}{it.notes ? ` · ${it.notes}` : ""}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono-nums text-slate-600 text-sm">{it.score}/5</span>
                    <span className={`h-7 w-7 rounded-lg flex items-center justify-center text-xs font-bold ${GRADE_CLS[it.grade]}`}>{it.grade}</span>
                    {canEdit && it.employee_id && (
                      <span role="button" tabIndex={0} data-testid={`eval-row-delete-${it.employee_id}`}
                        onClick={(e) => onDeleteOne(e, it)}
                        className="text-slate-300 hover:text-rose-600 transition p-1"><Trash2 className="h-4 w-4" /></span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
